import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  };

  return (
    <div
      className="relative h-[340px] md:h-[430px] rounded-lg overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {heroSlides.map((slide, slideIndex) => {
        const active = slideIndex === index;
        const to =
          slide.kind === "film" ? `/detail/shortfilm/${slide.id}` : `/detail/news/${slide.id}`;

        return (
          <Link
            key={slide.id}
            to={to}
            aria-hidden={!active}
            tabIndex={active ? 0 : -1}
            className={`absolute inset-0 block cursor-pointer transition-opacity duration-700 ${
              active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              title={`${slide.title} - 小电影焦点推荐`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/5" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-accent-500 text-background-50 text-[11px] font-semibold tracking-wide">
                <i className="ri-fire-fill text-[10px]" />
                {slide.badge}
              </span>
              <h2 className="mt-3 text-2xl md:text-[34px] font-extrabold leading-tight text-background-50 tracking-tight">
                {slide.title}
              </h2>
              <p className="mt-2 max-w-xl text-[13px] md:text-sm leading-relaxed text-background-200 line-clamp-2">
                {slide.description}
              </p>
              <div className="mt-3 flex items-center gap-3 text-[11px] md:text-xs text-background-300">
                <span className="font-label">{slide.meta}</span>
                <span className="hidden sm:inline-flex items-center gap-1 text-background-100">
                  查看详情
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </Link>
        );
      })}

      <div className="absolute z-20 top-4 right-4 flex items-center gap-1.5">
        <button
          type="button"
          aria-label="上一条推荐"
          onClick={() => goTo(index - 1)}
          className="w-8 h-8 rounded-md bg-foreground-950/40 hover:bg-foreground-950/70 backdrop-blur-sm flex items-center justify-center text-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          aria-label="下一条推荐"
          onClick={() => goTo(index + 1)}
          className="w-8 h-8 rounded-md bg-foreground-950/40 hover:bg-foreground-950/70 backdrop-blur-sm flex items-center justify-center text-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="absolute z-20 bottom-4 right-4 md:bottom-7 md:right-7 flex items-center gap-1.5">
        {heroSlides.map((slide, dotIndex) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${dotIndex + 1} 条推荐`}
            onClick={() => setIndex(dotIndex)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              dotIndex === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/60 hover:bg-background-50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}